import { useState, useCallback, useEffect } from 'react';
import { pusherService } from '../services/pusherService';
import { playNotificationSound } from '../lib/soundUtils';

// API Endpoint
const LINE_API_URL = process.env.REACT_APP_LINE_API_URL || 'https://booking.trailernchu.co.jp/sys/line/api_v2.php';
const API_KEY = 'nc-line-sync-key-2024';
const DISPATCH_API_PREFIX = process.env.REACT_APP_DISPATCH_API_URL || '/api';

// --- Types ---

interface LineUser {
    line_user_id: string;
    line_display_name?: string;
    line_picture_url?: string;
    unread_count: number;
    last_message_at?: string;
    last_message_content?: string;
    is_archived?: boolean;
    is_pinned?: boolean;
    [key: string]: any;
}

interface LineTag {
    id: string | number;
    name: string;
    color?: string;
    [key: string]: any;
}

interface LineMessage {
    id: string | number;
    line_message_id?: string;
    line_user_id: string;
    direction: 'inbound' | 'outbound';
    type: string;
    content: string;
    quote_token?: string | null;
    quoted_content?: string | null;
    reservation_id?: string | null;
    created_at: string;
    isOptimistic?: boolean;
    status?: string;
    translated_content?: string | null;
    detected_language?: string | null;
    [key: string]: any;
}

interface SharedState {
    users: LineUser[];
    tags: LineTag[];
    optimisticMessages: Record<string, LineMessage[]>;
    loading: boolean;
    activeStaffChats: LineUser[];
    pusherConnected: boolean;
}

type StateListener = (state: SharedState) => void;

interface SendResult {
    success: boolean;
    error?: string;
}

interface FetchMessagesResult {
    messages: LineMessage[];
    has_more: boolean;
}

interface UseLineChatReturn {
    users: LineUser[];
    tags: LineTag[];
    messages: Record<string, LineMessage[]>;
    optimisticMessages: Record<string, LineMessage[]>;
    loading: boolean;
    error: string | null;
    fetchUsers: (search?: string, includeArchived?: boolean) => Promise<LineUser[] | null>;
    fetchTags: () => Promise<void>;
    addTagMaster: (name: string, color?: string) => Promise<boolean>;
    deleteTagMaster: (tagId: string | number) => Promise<boolean>;
    fetchMessages: (lineUserId: string, beforeId?: string | null, limit?: number) => Promise<FetchMessagesResult>;
    sendMessage: (lineUserId: string, text: string, quoteToken?: string | null, quotedContent?: string | null, reservationId?: string | null) => Promise<SendResult>;
    uploadImage: (lineUserId: string, file: File) => Promise<boolean>;
    markRead: (lineUserId: string) => Promise<void>;
    updateProfile: (lineUserId: string, profileData: any) => Promise<boolean>;
    searchReservations: (query: string) => Promise<any[]>;
    linkReservation: (lineUserId: string, reservationId: string, reservationData?: any) => Promise<boolean>;
    unlinkReservation: (lineUserId: string, reservationId: string) => Promise<boolean>;
    togglePin: (lineUserId: string, isPinned: boolean) => Promise<boolean>;
    toggleArchive: (lineUserId: string, isArchived: boolean) => Promise<boolean>;
    fetchTemplates: () => Promise<any[]>;
    saveTemplate: (templateData: any) => Promise<boolean>;
    deleteTemplate: (templateId: string | number) => Promise<boolean>;
    fetchTemplateCategories: () => Promise<any[]>;
    saveTemplateCategory: (categoryData: any) => Promise<boolean>;
    deleteTemplateCategory: (categoryId: string | number) => Promise<boolean>;
    updateCategoryOrder: (orders: any[]) => Promise<boolean>;
    updateTemplateOrder: (orders: any[]) => Promise<boolean>;
    sendBroadcast: (lineUserIds: string[], text: string, messages?: any[] | null) => Promise<any>;
    syncProfile: (lineUserId: string) => Promise<boolean>;
    suggestReply: (lineUserId: string) => Promise<{ reply: string; action_type: string; confidence: number; reasoning: string } | null>;
    fetchMessageContext: (lineUserId: string, quoteToken: string, quotedMessageId?: string) => Promise<LineMessage[] | null>;
    translateMessage: (messageId: string | number, lineUserId: string, targetLang?: string) => Promise<{ translated_content: string; detected_language: string } | null>;
    sendTranslatedMessage: (lineUserId: string, text: string, targetLang: string, quoteToken?: string | null, quotedContent?: string | null, reservationId?: string | null) => Promise<SendResult>;
    agentCheckReservation: (lineUserId: string, reservationId?: string | null) => Promise<any | null>;
    agentCheckChange: (lineUserId: string, newReturnDatetime: string, reservationId?: string | null) => Promise<any | null>;
    agentCancelReservation: (lineUserId: string, reservationId: string | null, confirm: boolean) => Promise<any | null>;
    activeStaffChats: LineUser[];
    openStaffChat: (user: LineUser) => void;
    closeStaffChat: (lineUserId: string) => void;
    pusherConnected: boolean;
}

// Shared state across all hook instances
let sharedUsers: LineUser[] = [];
let sharedTags: LineTag[] = [];
let sharedOptimisticMessages: Record<string, LineMessage[]> = {};
let sharedLoading: boolean = false;
let sharedActiveStaffChats: LineUser[] = []; // Staff mini window state (persists across navigation)
let sharedPusherConnected: boolean = false;
let listeners: StateListener[] = [];
let lastRequestId: number = 0;

/** 現在チャット画面を開いているLINEユーザーID（通知音制御用） */
let sharedOpenLineUserId: string | null = null;

/** 現在開いているチャットのユーザーIDをセット/クリア */
export const setOpenLineUserId = (uid: string | null): void => {
    sharedOpenLineUserId = uid;
};

const notifyListeners = (): void => {
    const state: SharedState = {
        users: Array.isArray(sharedUsers) ? [...sharedUsers] : [],
        tags: Array.isArray(sharedTags) ? [...sharedTags] : [],
        optimisticMessages: JSON.parse(JSON.stringify(sharedOptimisticMessages || {})),
        loading: sharedLoading,
        activeStaffChats: [...sharedActiveStaffChats],
        pusherConnected: sharedPusherConnected,
    };
    listeners.forEach(listener => listener(state));
};

// ===== Pusher LINE Chat listener (shared, runs once) =====
let pusherUnsubscribe: (() => void) | null = null;

function initLinePusherListener(): void {
    if (pusherUnsubscribe) return; // already initialized

    pusherUnsubscribe = pusherService.subscribeLine((eventName: string, data: any) => {
        if (eventName === 'line.message.received') {
            const uid: string = data.line_user_id;
            // Update user in sharedUsers
            const userIdx = sharedUsers.findIndex(u => u.line_user_id === uid);
            if (userIdx >= 0) {
                sharedUsers = sharedUsers.map(u => {
                    if (u.line_user_id !== uid) return u;
                    return {
                        ...u,
                        unread_count: data.unread_count ?? (u.unread_count + 1),
                        last_message_at: data.created_at,
                        last_message_content: data.content,
                        is_archived: false,
                    };
                });
                notifyListeners();
            } else {
                // User not in list (e.g. was archived) — re-fetch to bring them back
                // Use dynamic import-free approach: trigger a custom event that LineChatBoard listens to
                window.dispatchEvent(new CustomEvent('line-pusher-refetch'));
            }

            // Dispatch custom event for message list updates
            window.dispatchEvent(new CustomEvent('line-pusher-message', { detail: data }));

            // Play notification sound only when the sender's chat is NOT currently open
            if (sharedOpenLineUserId !== uid) {
                playNotificationSound();
            }
        }

        if (eventName === 'line.message.sent') {
            const uid: string = data.line_user_id;
            // Update last_message_at in sharedUsers
            sharedUsers = sharedUsers.map(u => {
                if (u.line_user_id !== uid) return u;
                return { ...u, last_message_at: data.created_at, last_message_content: data.content };
            });
            notifyListeners();

            // Dispatch custom event for message list updates
            window.dispatchEvent(new CustomEvent('line-pusher-message', { detail: data }));
        }

        if (eventName === 'line.user.updated') {
            const uid: string = data.line_user_id;
            const field: string = data.field;
            const value: any = data.value;
            // Ignore remote archive — only the browser that initiated it should hide the chat.
            // Other browsers may still be chatting with this user.
            if (field === 'is_archived' && value === true) return;

            // 予約紐づけ・プロフィール変更はサーバーからユーザー再取得が必要
            if (field === 'reservation_links' || field === 'profile') {
                window.dispatchEvent(new CustomEvent('line-pusher-refetch'));
                return;
            }

            sharedUsers = sharedUsers.map(u => {
                if (u.line_user_id !== uid) return u;
                return { ...u, [field]: value };
            });
            notifyListeners();
        }
    });

    // Track Pusher connection state
    sharedPusherConnected = pusherService.getState().connected;
    const dispatchUnsub = pusherService.subscribe(() => {
        // Detect connection state changes
        const newState = pusherService.getState().connected;
        if (newState !== sharedPusherConnected) {
            sharedPusherConnected = newState;
            notifyListeners();
        }
    });

    // Store for potential cleanup
    pusherUnsubscribe = () => {
        if (typeof pusherUnsubscribe === 'function') pusherUnsubscribe();
        if (typeof dispatchUnsub === 'function') dispatchUnsub();
    };
}

// Auto-initialize when module loads
initLinePusherListener();

export const useLineChat = (): UseLineChatReturn => {
    const [users, setUsers] = useState<LineUser[]>(sharedUsers);
    const [tags, setTags] = useState<LineTag[]>(sharedTags);
    const [messages, setMessages] = useState<Record<string, LineMessage[]>>({});
    const [optimisticMessages, setOptimisticMessages] = useState<Record<string, LineMessage[]>>(sharedOptimisticMessages);
    const [loading, setLoading] = useState<boolean>(sharedLoading);
    const [activeStaffChats, setActiveStaffChats] = useState<LineUser[]>(sharedActiveStaffChats);
    const [pusherConnected, setPusherConnected] = useState<boolean>(sharedPusherConnected);
    const [error, setError] = useState<string | null>(null);

    // Sync local state with shared state
    useEffect(() => {
        const listener = (state: SharedState) => {
            setUsers(state.users);
            setTags(state.tags);
            setOptimisticMessages(state.optimisticMessages);
            setLoading(state.loading);
            setActiveStaffChats(state.activeStaffChats);
            setPusherConnected(state.pusherConnected);
        };
        listeners.push(listener);
        return () => {
            listeners = listeners.filter(l => l !== listener);
        };
    }, []);

    // Staff mini window management (shared across all pages)
    const openStaffChat = useCallback((user: LineUser) => {
        if (!sharedActiveStaffChats.find(c => c.line_user_id === user.line_user_id)) {
            // ウィンドウ幅の80%に収まるポップアップ数を計算（1個=320px+16pxギャップ、右マージン40px）
            const maxPopups = Math.max(1, Math.floor((window.innerWidth * 0.8 - 40) / (320 + 16)));
            sharedActiveStaffChats = [user, ...sharedActiveStaffChats].slice(0, maxPopups);
            notifyListeners();
        }
    }, []);

    const closeStaffChat = useCallback((lineUserId: string) => {
        sharedActiveStaffChats = sharedActiveStaffChats.filter(c => c.line_user_id !== lineUserId);
        notifyListeners();
    }, []);

    const fetchUsers = useCallback(async (search: string = '', includeArchived: boolean = false): Promise<LineUser[] | null> => {
        const requestId = ++lastRequestId;
        sharedLoading = true;
        notifyListeners();
        try {
            const queryParam = search ? `&search=${encodeURIComponent(search)}` : '';
            const archiveParam = includeArchived ? `&include_archived=1` : '';
            const res = await fetch(`${LINE_API_URL}?action=get_users&api_key=${API_KEY}${queryParam}${archiveParam}`);
            const data = await res.json();

            // 検索リクエストの場合のみ stale check（初期ロードでは常に受け入れる）
            if (search && requestId !== lastRequestId) {
                return null;
            }

            if (data.success) {
                sharedUsers = Array.isArray(data.users) ? data.users : [];
                notifyListeners();
                return sharedUsers;
            } else {
                setError(data.error);
                return null;
            }
        } catch (err: any) {
            setError(err.message);
            return null;
        } finally {
            if (requestId === lastRequestId) {
                sharedLoading = false;
                notifyListeners();
            }
        }
    }, []);

    const fetchTags = useCallback(async (): Promise<void> => {
        try {
            const res = await fetch(`${LINE_API_URL}?action=get_tags&api_key=${API_KEY}`);
            const data = await res.json();
            if (data.success) {
                sharedTags = Array.isArray(data.tags) ? data.tags : [];
                notifyListeners();
            }
        } catch (err) {
            console.error('Failed to fetch tags:', err);
        }
    }, []);

    const addTagMaster = useCallback(async (name: string, color: string = '#3b82f6'): Promise<boolean> => {
        try {
            const url = `${LINE_API_URL}?action=update_tags_master&api_key=${API_KEY}`;
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sub_action: 'add', name, color })
            });
            const data = await res.json();
            if (data.success) {
                await fetchTags();
                return true;
            }
        } catch (err) {
            console.error('[DEBUG] Failed to add tag master:', err);
        }
        return false;
    }, [fetchTags]);

    const deleteTagMaster = useCallback(async (tagId: string | number): Promise<boolean> => {
        try {
            const url = `${LINE_API_URL}?action=update_tags_master&api_key=${API_KEY}`;
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sub_action: 'delete', id: tagId })
            });
            const data = await res.json();
            if (data.success) {
                await fetchTags();
                return true;
            }
        } catch (err) {
            console.error('[DEBUG] Failed to delete tag master:', err);
        }
        return false;
    }, [fetchTags]);

    const fetchMessages = useCallback(async (lineUserId: string, beforeId: string | null = null, limit: number = 200): Promise<FetchMessagesResult> => {
        try {
            const url = `${LINE_API_URL}?action=get_messages&line_user_id=${lineUserId}&api_key=${API_KEY}&limit=${limit}${beforeId ? `&before_id=${beforeId}` : ''}`;
            const res = await fetch(url);
            const data = await res.json();
            if (data.success) {
                const newMessages: LineMessage[] = data.messages;

                if (beforeId) {
                    // 古いメッセージを読み込んだ場合（上読み）：既存の前に結合
                    setMessages(prev => {
                        const existing = prev[lineUserId] || [];
                        const combined = [...newMessages, ...existing].filter((v, i, a) =>
                            a.findIndex(t => (t.line_message_id && t.line_message_id === v.line_message_id) || (t.id === v.id)) === i
                        );
                        return { ...prev, [lineUserId]: combined };
                    });
                } else {
                    // 初期読み込み or 最新チェック（ポーリング）：既存の後に結合して重複排除
                    setMessages(prev => {
                        const existing = prev[lineUserId] || [];
                        const combined = [...existing, ...newMessages].filter((v, i, a) =>
                            a.findIndex(t => (t.line_message_id && t.line_message_id === v.line_message_id) || (t.id === v.id)) === i
                        );
                        return { ...prev, [lineUserId]: combined };
                    });

                    // Clear optimistic messages that are now confirmed by the server
                    const currentOpts = sharedOptimisticMessages[lineUserId] || [];
                    const filteredOpts = currentOpts.filter(opt =>
                        !newMessages.some(m => m.content === opt.content && m.direction === 'outbound')
                    );
                    sharedOptimisticMessages[lineUserId] = filteredOpts;
                    notifyListeners();
                }

                return { messages: newMessages, has_more: data.has_more };
            }
        } catch (err) {
            console.error('Failed to fetch messages:', err);
        }
        return { messages: [], has_more: false };
    }, []);

    // 引用元が現在の履歴にない場合に、DBから遡って履歴を補完する
    const fetchMessageContext = useCallback(async (lineUserId: string, quoteToken: string, quotedMessageId: string = ''): Promise<LineMessage[] | null> => {
        try {
            const url = `${LINE_API_URL}?action=get_message_context&line_user_id=${lineUserId}&quote_token=${encodeURIComponent(quoteToken)}&quoted_message_id=${quotedMessageId}&api_key=${API_KEY}`;
            const res = await fetch(url);
            const data = await res.json();
            if (data.success) {
                const contextMessages: LineMessage[] = data.messages;
                setMessages(prev => {
                    const existing = prev[lineUserId] || [];
                    const combined = [...contextMessages, ...existing].filter((v, i, a) =>
                        a.findIndex(t => (t.line_message_id && t.line_message_id === v.line_message_id) || (t.id === v.id)) === i
                    ).sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
                    return { ...prev, [lineUserId]: combined };
                });
                return contextMessages;
            }
        } catch (err) {
            console.error('Failed to fetch message context:', err);
        }
        return null;
    }, []);

    const translateMessage = useCallback(async (messageId: string | number, lineUserId: string, targetLang: string = 'ja'): Promise<{ translated_content: string; detected_language: string } | null> => {
        try {
            const url = `${LINE_API_URL}?action=translate_message&message_id=${messageId}&line_user_id=${encodeURIComponent(lineUserId)}&target_lang=${encodeURIComponent(targetLang)}&api_key=${API_KEY}`;
            const res = await fetch(url);
            const rawText = await res.text();
            let data: any;
            try { data = JSON.parse(rawText); } catch { console.error('[TRANSLATE] JSON parse error:', rawText); return null; }
            if (data.success) {
                // ローカルのメッセージキャッシュも更新
                setMessages(prev => {
                    const msgs = prev[lineUserId] || [];
                    const updated = msgs.map(m =>
                        String(m.id) === String(messageId) || m.line_message_id === String(messageId)
                            ? { ...m, translated_content: data.translated_content, detected_language: data.detected_language }
                            : m
                    );
                    return { ...prev, [lineUserId]: updated };
                });
                return { translated_content: data.translated_content, detected_language: data.detected_language };
            } else {
                console.error('[TRANSLATE] API returned error:', data.error, data);
            }
        } catch (err) {
            console.error('[TRANSLATE] fetch error:', err);
        }
        return null;
    }, []);

    const sendTranslatedMessage = useCallback(async (lineUserId: string, text: string, targetLang: string, quoteToken: string | null = null, quotedContent: string | null = null, reservationId: string | null = null): Promise<SendResult> => {
        const optId = `opt-tr-${Date.now()}`;
        const optMsg: LineMessage = {
            id: optId,
            line_user_id: lineUserId,
            reservation_id: reservationId,
            direction: 'outbound',
            type: 'text',
            content: text,
            translated_content: '翻訳中...',
            detected_language: targetLang,
            quote_token: quoteToken,
            quoted_content: quotedContent,
            created_at: new Date().toISOString(),
            isOptimistic: true,
            status: 'sending'
        };
        sharedOptimisticMessages[lineUserId] = [...(sharedOptimisticMessages[lineUserId] || []), optMsg];
        notifyListeners();

        try {
            const res = await fetch(`${LINE_API_URL}?action=send_translated_message&api_key=${API_KEY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    line_user_id: lineUserId,
                    text,
                    target_lang: targetLang,
                    quote_token: quoteToken,
                    quoted_content: quotedContent,
                    reservation_id: reservationId,
                }),
            });
            const data = await res.json();
            if (data.success) {
                sharedOptimisticMessages[lineUserId] = (sharedOptimisticMessages[lineUserId] || []).map(m =>
                    m.id === optId ? { ...m, status: 'sent', translated_content: data.translated_text } : m
                );
                notifyListeners();
                await fetchMessages(lineUserId, null, 20);
                return { success: true };
            } else {
                sharedOptimisticMessages[lineUserId] = (sharedOptimisticMessages[lineUserId] || []).filter(m => m.id !== optId);
                notifyListeners();
                return { success: false, error: data.error || 'Unknown error' };
            }
        } catch (err) {
            sharedOptimisticMessages[lineUserId] = (sharedOptimisticMessages[lineUserId] || []).filter(m => m.id !== optId);
            notifyListeners();
            return { success: false, error: String(err) };
        }
    }, [fetchMessages]);

    // ── エージェント関数 ──────────────────────────────────────────
    const agentCheckReservation = useCallback(async (lineUserId: string, reservationId?: string | null): Promise<any | null> => {
        try {
            const params = new URLSearchParams({ action: 'agent_check_reservation', line_user_id: lineUserId, api_key: API_KEY });
            if (reservationId) params.set('reservation_id', reservationId);
            const res = await fetch(`${LINE_API_URL}?${params}`);
            const data = await res.json();
            if (data.success) return data;
            console.error('[AGENT] check_reservation failed:', data.error);
        } catch (err) { console.error('[AGENT] check_reservation error:', err); }
        return null;
    }, []);

    const agentCheckChange = useCallback(async (lineUserId: string, newReturnDatetime: string, reservationId?: string | null): Promise<any | null> => {
        try {
            const params = new URLSearchParams({ action: 'agent_check_change', line_user_id: lineUserId, new_return_datetime: newReturnDatetime, api_key: API_KEY });
            if (reservationId) params.set('reservation_id', reservationId);
            const res = await fetch(`${LINE_API_URL}?${params}`);
            const data = await res.json();
            if (data.success) return data;
            console.error('[AGENT] check_change failed:', data.error);
        } catch (err) { console.error('[AGENT] check_change error:', err); }
        return null;
    }, []);

    const agentCancelReservation = useCallback(async (lineUserId: string, reservationId: string | null, confirm: boolean): Promise<any | null> => {
        try {
            const res = await fetch(`${LINE_API_URL}?action=agent_cancel_reservation&api_key=${API_KEY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line_user_id: lineUserId, reservation_id: reservationId, confirm }),
            });
            const data = await res.json();
            if (data.success) return data;
            console.error('[AGENT] cancel failed:', data.error);
        } catch (err) { console.error('[AGENT] cancel error:', err); }
        return null;
    }, []);
    // ─────────────────────────────────────────────────────────────

    const sendMessage = useCallback(async (lineUserId: string, text: string, quoteToken: string | null = null, quotedContent: string | null = null, reservationId: string | null = null): Promise<SendResult> => {
        const optId = `opt-${Date.now()}`;
        const optMsg: LineMessage = {
            id: optId,
            line_user_id: lineUserId,
            reservation_id: reservationId, // Store reservation_id in optimistic message too
            direction: 'outbound',
            type: 'text',
            content: text,
            quote_token: quoteToken,
            quoted_content: quotedContent,
            created_at: new Date().toISOString(),
            isOptimistic: true,
            status: 'sending'
        };

        sharedOptimisticMessages[lineUserId] = [...(sharedOptimisticMessages[lineUserId] || []), optMsg];
        notifyListeners();

        try {
            const payload = {
                line_user_id: lineUserId,
                text,
                quote_token: quoteToken,
                quoted_content: quotedContent,
                reservation_id: reservationId
            };
            const res = await fetch(`${LINE_API_URL}?action=send_message&api_key=${API_KEY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (data.success) {
                sharedOptimisticMessages[lineUserId] = (sharedOptimisticMessages[lineUserId] || []).map(m =>
                    m.id === optId ? { ...m, status: 'sent' } : m
                );
                notifyListeners();
                await fetchMessages(lineUserId);
                return { success: true };
            } else {
                const errorMsg = data.error || 'Unknown error';
                console.error(`[DEBUG-SEND] API Error: ${errorMsg}`);
                sharedOptimisticMessages[lineUserId] = (sharedOptimisticMessages[lineUserId] || []).filter(m => m.id !== optId);
                notifyListeners();
                return { success: false, error: errorMsg };
            }
        } catch (err: any) {
            console.error('[DEBUG-SEND] Fetch Exception:', err);
            sharedOptimisticMessages[lineUserId] = (sharedOptimisticMessages[lineUserId] || []).filter(m => m.id !== optId);
            notifyListeners();
            return { success: false, error: err.message };
        }
    }, [fetchMessages]);

    const markRead = useCallback(async (lineUserId: string): Promise<void> => {
        try {
            const res = await fetch(`${LINE_API_URL}?action=mark_read&line_user_id=${lineUserId}&api_key=${API_KEY}`);
            const data = await res.json();
            sharedUsers = sharedUsers.map(u =>
                u.line_user_id === lineUserId ? { ...u, unread_count: 0 } : u
            );
            notifyListeners();
        } catch (err) {
            console.error('[DEBUG-READ] Failed to mark read:', err);
        }
    }, []);

    const updateProfile = useCallback(async (lineUserId: string, profileData: any): Promise<boolean> => {
        try {
            const url = `${LINE_API_URL}?action=update_profile&api_key=${API_KEY}`;
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line_user_id: lineUserId, ...profileData })
            });
            const data = await res.json();
            if (data.success) {
                await fetchUsers(); // Refresh user list
                return true;
            } else {
                console.error('[DEBUG] Profile update failed:', data.error);
                return false;
            }
        } catch (err) {
            console.error('[DEBUG] Failed to update profile:', err);
            return false;
        }
    }, [fetchUsers]);

    const searchReservations = useCallback(async (query: string): Promise<any[]> => {
        if (!query) return [];
        try {
            const res = await fetch(`${LINE_API_URL}?action=search_reservations&query=${encodeURIComponent(query)}&api_key=${API_KEY}`);
            const data = await res.json();
            return data.success ? data.results : [];
        } catch (err) {
            console.error('Failed to search reservations:', err);
            return [];
        }
    }, []);

    const linkReservation = useCallback(async (lineUserId: string, reservationId: string, _reservationData: any = null): Promise<boolean> => {
        try {
            const res = await fetch(`${LINE_API_URL}?action=link_reservation&api_key=${API_KEY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    line_user_id: lineUserId,
                    reservation_id: reservationId,
                })
            });
            const data = await res.json();
            if (data.success) {
                await fetchUsers();
                return true;
            }
        } catch (err) {
            console.error('[DEBUG-LINK] Failed to link reservation:', err);
        }
        return false;
    }, [fetchUsers]);

    const unlinkReservation = useCallback(async (lineUserId: string, reservationId: string): Promise<boolean> => {
        try {
            const res = await fetch(`${LINE_API_URL}?action=unlink_reservation&api_key=${API_KEY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line_user_id: lineUserId, reservation_id: reservationId })
            });
            const data = await res.json();
            if (data.success) {
                await fetchUsers();
                return true;
            }
        } catch (err) {
            console.error('[DEBUG-LINK] Failed to unlink reservation:', err);
        }
        return false;
    }, [fetchUsers]);

    const togglePin = useCallback(async (lineUserId: string, isPinned: boolean): Promise<boolean> => {
        // Optimistic update
        const originalUsers = [...sharedUsers];
        sharedUsers = sharedUsers.map(u =>
            u.line_user_id === lineUserId ? { ...u, is_pinned: isPinned } : u
        );
        notifyListeners();

        try {
            const res = await fetch(`${LINE_API_URL}?action=toggle_pin&api_key=${API_KEY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line_user_id: lineUserId, is_pinned: isPinned })
            });
            const data = await res.json();
            if (!data.success) {
                sharedUsers = originalUsers;
                notifyListeners();
                return false;
            }
            return true;
        } catch (err) {
            console.error('Failed to toggle pin:', err);
            sharedUsers = originalUsers;
            notifyListeners();
            return false;
        }
    }, []);

    const toggleArchive = useCallback(async (lineUserId: string, isArchived: boolean): Promise<boolean> => {
        // Optimistic update: remove from list if archiving
        const originalUsers = [...sharedUsers];
        if (isArchived) {
            sharedUsers = sharedUsers.filter(u => u.line_user_id !== lineUserId);
        } else {
            sharedUsers = sharedUsers.map(u =>
                u.line_user_id === lineUserId ? { ...u, is_archived: false } : u
            );
        }
        notifyListeners();

        try {
            const res = await fetch(`${LINE_API_URL}?action=toggle_archive&api_key=${API_KEY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line_user_id: lineUserId, is_archived: isArchived })
            });
            const data = await res.json();
            if (!data.success) {
                sharedUsers = originalUsers;
                notifyListeners();
                return false;
            }
            return true;
        } catch (err) {
            console.error('Failed to toggle archive:', err);
            sharedUsers = originalUsers;
            notifyListeners();
            return false;
        }
    }, []);

    const fetchTemplates = useCallback(async (): Promise<any[]> => {
        try {
            const res = await fetch(`${LINE_API_URL}?action=get_templates&api_key=${API_KEY}`);
            const data = await res.json();
            return data.success ? data.templates : [];
        } catch (err) {
            console.error('Failed to fetch templates:', err);
            return [];
        }
    }, []);

    const saveTemplate = useCallback(async (templateData: any): Promise<boolean> => {
        try {
            const res = await fetch(`${LINE_API_URL}?action=save_template&api_key=${API_KEY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(templateData)
            });
            const data = await res.json();
            return data.success;
        } catch (err) {
            console.error('Failed to save template:', err);
            return false;
        }
    }, []);

    const deleteTemplate = useCallback(async (templateId: string | number): Promise<boolean> => {
        try {
            const res = await fetch(`${LINE_API_URL}?action=delete_template&id=${templateId}&api_key=${API_KEY}`);
            const data = await res.json();
            return data.success;
        } catch (err) {
            console.error('Failed to delete template:', err);
            return false;
        }
    }, []);

    const fetchTemplateCategories = useCallback(async (): Promise<any[]> => {
        try {
            const res = await fetch(`${LINE_API_URL}?action=get_template_categories&api_key=${API_KEY}`);
            const data = await res.json();
            return data.success ? data.categories : [];
        } catch (err) {
            console.error('Failed to fetch categories:', err);
            return [];
        }
    }, []);

    const saveTemplateCategory = useCallback(async (categoryData: any): Promise<boolean> => {
        try {
            const res = await fetch(`${LINE_API_URL}?action=save_template_category&api_key=${API_KEY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(categoryData)
            });
            const data = await res.json();
            return data.success;
        } catch (err) {
            console.error('Failed to save category:', err);
            return false;
        }
    }, []);

    const deleteTemplateCategory = useCallback(async (categoryId: string | number): Promise<boolean> => {
        try {
            const res = await fetch(`${LINE_API_URL}?action=delete_template_category&id=${categoryId}&api_key=${API_KEY}`);
            const data = await res.json();
            return data.success;
        } catch (err) {
            console.error('Failed to delete category:', err);
            return false;
        }
    }, []);

    const updateCategoryOrder = useCallback(async (orders: any[]): Promise<boolean> => {
        try {
            const res = await fetch(`${LINE_API_URL}?action=update_category_order&api_key=${API_KEY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orders })
            });
            const data = await res.json();
            return data.success;
        } catch (err) {
            console.error('Failed to update category order:', err);
            return false;
        }
    }, []);

    const updateTemplateOrder = useCallback(async (orders: any[]): Promise<boolean> => {
        try {
            const res = await fetch(`${LINE_API_URL}?action=update_template_order&api_key=${API_KEY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orders })
            });
            const data = await res.json();
            return data.success;
        } catch (err) {
            console.error('Failed to update template order:', err);
            return false;
        }
    }, []);

    const syncProfile = useCallback(async (lineUserId: string): Promise<boolean> => {
        try {
            const res = await fetch(`${LINE_API_URL}?action=sync_profile&line_user_id=${lineUserId}&api_key=${API_KEY}`);
            const text = await res.text();
            let data: any;
            try {
                data = JSON.parse(text);
            } catch (e) {
                console.error(`[DEBUG-SYNC] JSON Parse Error for ${lineUserId}:`, text);
                return false;
            }

            if (data.success) {
                // Update local users list if found
                sharedUsers = sharedUsers.map(u =>
                    u.line_user_id === lineUserId ? {
                        ...u,
                        line_picture_url: data.profile.pictureUrl || u.line_picture_url,
                        line_display_name: data.profile.displayName || u.line_display_name
                    } : u
                );
                notifyListeners();
                return true;
            }
        } catch (err) {
            // Profile sync errors are non-critical (migration errors, network issues)
        }
        return false;
    }, []);

    const uploadImage = useCallback(async (lineUserId: string, file: File): Promise<boolean> => {
        const formData = new FormData();
        formData.append('line_user_id', lineUserId);
        formData.append('image', file);

        try {
            const res = await fetch(`${LINE_API_URL}?action=upload_image&api_key=${API_KEY}`, {
                method: 'POST',
                body: formData
            });
            const data = await res.json();
            if (data.success) {
                await fetchMessages(lineUserId);
                return true;
            } else {
                console.error('Upload failed:', data.error);
            }
        } catch (err) {
            console.error('Failed to upload image:', err);
        }
        return false;
    }, [fetchMessages]);

    const suggestReply = useCallback(async (lineUserId: string): Promise<{ reply: string; action_type: string; confidence: number; reasoning: string } | null> => {
        try {
            const res = await fetch(`${LINE_API_URL}?action=suggest_reply&line_user_id=${lineUserId}&api_key=${API_KEY}`);
            const data = await res.json();
            console.log('suggestReply response:', JSON.stringify(data));
            if (data.success) {
                return { reply: data.reply || '', action_type: data.action_type, confidence: data.confidence, reasoning: data.reasoning };
            } else {
                console.error('suggestReply API error:', data);
            }
        } catch (err) {
            console.error('Failed to suggest reply:', err);
        }
        return null;
    }, []);

    const sendBroadcast = useCallback(async (lineUserIds: string[], text: string, messages: any[] | null = null): Promise<any> => {
        try {
            const payload: any = { line_user_ids: lineUserIds, text };
            if (messages) payload.messages = messages;

            const res = await fetch(`${LINE_API_URL}?action=send_broadcast&api_key=${API_KEY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success !== undefined) {
                // Refresh relevant states if needed
                await fetchUsers();
                return data;
            }
        } catch (err) {
            console.error('Failed to send broadcast:', err);
        }
        return null;
    }, [fetchUsers]);

    return {
        users,
        tags,
        messages,
        optimisticMessages,
        loading,
        error,
        fetchUsers,
        fetchTags,
        addTagMaster,
        deleteTagMaster,
        fetchMessages,
        sendMessage,
        uploadImage,
        markRead,
        updateProfile,
        searchReservations,
        linkReservation,
        unlinkReservation,
        togglePin,
        toggleArchive,
        fetchTemplates,
        saveTemplate,
        deleteTemplate,
        fetchTemplateCategories,
        saveTemplateCategory,
        deleteTemplateCategory,
        updateCategoryOrder,
        updateTemplateOrder,
        sendBroadcast,
        syncProfile,
        suggestReply,
        fetchMessageContext,
        translateMessage,
        sendTranslatedMessage,
        agentCheckReservation,
        agentCheckChange,
        agentCancelReservation,
        activeStaffChats,
        openStaffChat,
        closeStaffChat,
        pusherConnected,
    };
};
